/* Thin top progress bar for navigations and in-page fetches (perf plan B4).
   Starts on: same-origin link clicks (no modifier keys / new tab / download / hash), GET form submits, and
   document events `mh:busy` (category filter, add-to-cart). Ends on `mh:idle`, on a 4 s watchdog when a click
   turned out to be handled in-page (drawer, dialog), or when the page returns from the bfcache.
   No `beforeunload` listener on purpose: it would make the page ineligible for the back/forward cache in Safari.
   The bar eases to ~85 % and waits; `done` fills and fades. Reduced motion: plain show/hide. */
(() => {
  if (window.__mhProgress) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let bar = null;
  let busy = 0;
  let finishTimer = null;
  let watchdog = null;
  let lastClickAt = 0;

  const ensure = () => {
    if (bar) return bar;
    bar = document.createElement('div');
    bar.className = 'mh-progress';
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-label', 'Loading');
    bar.setAttribute('aria-hidden', 'true');
    bar.dataset.state = 'idle';
    document.body.appendChild(bar);
    return bar;
  };

  const show = () => {
    const el = ensure();
    clearTimeout(finishTimer);
    if (el.dataset.state === 'active') return;
    el.dataset.state = 'idle';
    void el.offsetWidth; // restart the CSS animation from 0
    el.classList.toggle('mh-progress--reduced', reduced.matches);
    el.dataset.state = 'active';
  };

  const hide = () => {
    if (!bar || bar.dataset.state !== 'active') return;
    bar.dataset.state = 'done';
    finishTimer = setTimeout(() => {
      if (bar && bar.dataset.state === 'done') bar.dataset.state = 'idle';
    }, reduced.matches ? 0 : 400);
  };

  const start = () => {
    busy += 1;
    show();
  };

  const done = () => {
    busy = Math.max(0, busy - 1);
    if (busy === 0) hide();
  };

  const reset = () => {
    busy = 0;
    clearTimeout(watchdog);
    if (bar) bar.dataset.state = 'idle';
  };

  document.addEventListener('mh:busy', () => {
    // A click that we already counted as a navigation turned out to be an in-page fetch: absorb it.
    if (Date.now() - lastClickAt < 300 && busy > 0) {
      lastClickAt = 0;
      clearTimeout(watchdog);
      return;
    }
    start();
  });
  document.addEventListener('mh:idle', done);

  const navigationIntent = () => {
    lastClickAt = Date.now();
    start();
    clearTimeout(watchdog);
    // Still here after 4 s and nothing reported busy → the click was handled in-page; drop our count.
    watchdog = setTimeout(() => {
      if (Date.now() - lastClickAt >= 3900) done();
    }, 4000);
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link) return;
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download') || link.dataset.mhNoProgress !== undefined) return;
    const href = link.getAttribute('href') || '';
    if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;
    let url;
    try { url = new URL(link.href, window.location.href); } catch { return; }
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
    // Let in-page handlers (drawers, filters, dialogs) preventDefault first.
    setTimeout(() => { if (!event.defaultPrevented) navigationIntent(); }, 0);
  });

  document.addEventListener('submit', (event) => {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if ((form.method || 'get').toLowerCase() !== 'get' || form.target) return;
    setTimeout(() => { if (!event.defaultPrevented) navigationIntent(); }, 0);
  });

  window.addEventListener('pageshow', (event) => { if (event.persisted) reset(); });

  window.__mhProgress = { start, done, reset };
})();
